import React from "react";
import { Box, Flex, Image, Link, Text } from "@chakra-ui/react";
import { Trans, useTranslation } from "react-i18next";
import emptyInbox from "@/assets/emptyInbox.svg?url";
import { colours } from "@/theme/colours";
import { fonts } from "@/theme/fonts";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { MAX_ACTIVE_RULES } from "../constants";
import { LibraryList } from "../LibraryList";
import { RuleSourceById } from "../RuleRelationshipTags";
import { AppliedList, RuleAction } from "../types";
import { isOwnAppliedList, isQuickBlockList } from "../utils";
import { InClassRuleRow } from "./InClassRuleRow";
import { InClassRule } from "./types";
import { isAppControlRule, countActiveRules } from "./utils";

const sectionLabelProps = {
	fontFamily: fonts.body,
	fontSize: "md",
	fontWeight: 400,
	color: colours.text.regular,
};

interface Props {
	inClassRules: InClassRule[];
	lists: AppliedList[];
	onCreateRule: () => void;
	onBrowseLibrary: () => void;
	onChangeInClassAction: (id: string, action: RuleAction) => void;
	onChangeInClassEnabled: (id: string, enabled: boolean) => void;
	onAddInClassToList: (ruleId: string, listId: string) => void;
	onEditInClassRule: (rule: InClassRule) => void;
	onDeleteInClassRule: (id: string) => void;
	onUnapplyList: (id: string) => void;
	/** The applied list whose remove-from-class write is in flight. */
	removingListId?: string;
	/** The in-class / app-control rows whose toggle writes are in flight. */
	updatingRuleIds?: string[];
	/** The in-class / app-control rows whose delete writes are in flight. */
	deletingRuleIds?: string[];
	/** The in-class rows whose add-to-list writes are in flight. */
	addingToListRuleIds?: string[];
}

export function ActiveRulesTab(props: Props): JSX.Element {
	const { t } = useTranslation();
	const {
		inClassRules,
		lists,
		onCreateRule,
		onBrowseLibrary,
		onChangeInClassAction,
		onChangeInClassEnabled,
		onAddInClassToList,
		onEditInClassRule,
		onDeleteInClassRule,
		onUnapplyList,
		removingListId,
		updatingRuleIds = [],
		deletingRuleIds = [],
		addingToListRuleIds = [],
	} = props;
	const currentQoriaUserId = useUserSessionStore((state) => state.userinfo?.qoriaUserId);
	const ownLists = lists.filter((list) => isOwnAppliedList(list, currentQoriaUserId));
	const classroomRules = inClassRules.filter((rule) => !isAppControlRule(rule));
	const appControlRules = inClassRules.filter(isAppControlRule);
	const ruleSourceById: RuleSourceById = Object.fromEntries([
		...classroomRules.map((rule) => [rule.id, { kind: "inClass" } as const]),
		...lists.flatMap((list) => {
			const name = isQuickBlockList(list) ? t("rulesLibrary.quickBlock.listTitle") : list.title;
			return list.rules.map((rule) => [rule.id, { kind: "list", name } as const]);
		}),
	]);
	const totalRules = countActiveRules(inClassRules, lists);
	const isEmpty = inClassRules.length === 0 && lists.length === 0;
	const rulesTotal = `${totalRules} / ${MAX_ACTIVE_RULES}`;
	let totalOn: "inClass" | "appControls" | "lists" = "lists";
	if (classroomRules.length > 0) {
		totalOn = "inClass";
	} else if (appControlRules.length > 0) {
		totalOn = "appControls";
	}

	function ruleRows(rules: InClassRule[]): JSX.Element[] {
		return rules.map((rule) => (
			<InClassRuleRow
				key={rule.id}
				rule={rule}
				libraryLists={ownLists}
				onChangeAction={(action) => onChangeInClassAction(rule.id, action)}
				onChangeEnabled={(enabled) => onChangeInClassEnabled(rule.id, enabled)}
				onAddToList={(listId) => onAddInClassToList(rule.id, listId)}
				onEdit={() => onEditInClassRule(rule)}
				onDelete={() => onDeleteInClassRule(rule.id)}
				isUpdating={updatingRuleIds.includes(rule.id)}
				isDeleting={deletingRuleIds.includes(rule.id)}
				isAddingToList={addingToListRuleIds.includes(rule.id)}
				ruleSourceById={ruleSourceById}
			/>
		));
	}

	return (
		<>
			{isEmpty ? (
				<Box textAlign="center" mt={150} data-testid="empty-class-rules">
					<Image src={emptyInbox} alt={t("rulesLibrary.activeRules.emptyAlt")} mx="auto" mb={20} />
					<Text fontSize="md" fontWeight="bold" color="neutrals.300">
						{t("rulesLibrary.activeRules.emptyTitle")}
					</Text>
					<Text fontSize="sm" color="neutrals.300">
						<Trans
							i18nKey="rulesLibrary.activeRules.emptyHint"
							components={{
								createRule: (
									<Link
										as="button"
										type="button"
										onClick={onCreateRule}
										fontSize="sm"
										fontWeight={400}
										color="neutrals.300"
										textDecoration="underline"
										verticalAlign="baseline"
									/>
								),
							}}
						/>
					</Text>
					<Link
						as="button"
						type="button"
						onClick={onBrowseLibrary}
						fontSize="sm"
						fontWeight={400}
						color="neutrals.300"
						textDecoration="underline"
						mt={1}
					>
						{t("rulesLibrary.activeRules.browseLibrary")}
					</Link>
				</Box>
			) : (
				<>
					{classroomRules.length > 0 && (
						<BoxSection title={t("rulesLibrary.activeRules.inClassRules")} count={totalOn === "inClass" ? rulesTotal : undefined}>
							{ruleRows(classroomRules)}
						</BoxSection>
					)}
					{appControlRules.length > 0 && (
						<BoxSection
							title={t("rulesLibrary.activeRules.appControls")}
							count={totalOn === "appControls" ? rulesTotal : undefined}
							spaced={classroomRules.length > 0}
						>
							{ruleRows(appControlRules)}
						</BoxSection>
					)}
					{lists.length > 0 && (
						<BoxSection
							title={t("rulesLibrary.activeRules.ruleLists")}
							count={totalOn === "lists" ? rulesTotal : undefined}
							spaced={classroomRules.length > 0 || appControlRules.length > 0}
						>
							<LibraryList
								lists={lists}
								onDelete={onUnapplyList}
								removingListId={removingListId}
								showEdit={false}
								confirmDelete={false}
								ruleSourceById={ruleSourceById}
							/>
						</BoxSection>
					)}
				</>
			)}
		</>
	);
}

function BoxSection(props: { title: string; count?: string; spaced?: boolean; children: React.ReactNode }): JSX.Element {
	return (
		<>
			<Flex align="center" gap={2} justify="space-between" mb={4} mt={props.spaced ? 5 : 0}>
				<Text {...sectionLabelProps}>{props.title}</Text>
				{props.count !== undefined && (
					<Text fontFamily={fonts.body} fontSize="sm" fontWeight={400} color={colours.text.regular}>
						{props.count}
					</Text>
				)}
			</Flex>
			{props.children}
		</>
	);
}
